// My Accounts' Filters dropdown: its groups of multi-value selects and date ranges, inside the
// shared shell (`FiltersDropdown.tsx`, also All accounts'). Two values in one dimension are two
// chips, so dropping United Kingdom does not drop United States with it.

import type { BookAccount } from '../api/types';
import {
  DATE_RANGE_FILTERS, FILTERS, FILTER_GROUPS, activeFilterCount, addFilterValue, dateRangeChip,
  emptyDateRange, emptyFilters, isDateRangeActive, removeFilterValue,
} from './filters';
import type { DateRangeFilter, FilterSpec, FilterState } from './filters';
import { pacificToday } from '../lib/format';
import { FilterChip, FiltersDropdown } from './FiltersDropdown';

function setDateBound(
  filters: FilterState,
  key: 'added' | 'contacted',
  bound: keyof DateRangeFilter,
  value: string,
): FilterState {
  return { ...filters, [key]: { ...filters[key], [bound]: value } };
}

// One select. Always empty: picking a value appends it and the select snaps back, which is
// what lets a second country land without replacing the first. Native select stays, because
// Country is typed by first letter.
function FilterSelect({
  filter,
  scopedRows,
  filters,
  onChange,
  today,
}: {
  filter: FilterSpec;
  scopedRows: BookAccount[];
  filters: FilterState;
  onChange: (next: FilterState) => void;
  today: string;
}) {
  const options = filter.build(scopedRows, today);
  // Nothing in scope carries this field, so the control would be a dead row.
  if (!options.length) return null;
  const selected = filters[filter.key];
  return (
    <div className="ffield">
      <label className="flab" htmlFor={filter.key}>
        {filter.label}
      </label>
      <select
        id={filter.key}
        value=""
        onChange={event => onChange(addFilterValue(filters, filter.key, event.target.value))}
      >
        <option value="">{selected.length ? 'Add another' : 'Any'}</option>
        {options.map(option => (
          <option
            key={option.value}
            value={option.value}
            disabled={selected.includes(option.value)}
          >
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export function FilterControls({
  scopedRows,
  filters,
  onChange,
  isOpen,
  onOpenChange,
  today = '',
}: {
  // The rows the *owner scope* leaves, not the filtered ones. Options and their counts derive
  // from what is in scope -- a country only one rep's accounts carry must not stay on offer
  // once the board is showing somebody else's.
  scopedRows: BookAccount[];
  filters: FilterState;
  onChange: (next: FilterState) => void;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  // Pacific calendar day from the book payload. "New today" is measured against the mirror,
  // not the laptop.
  today?: string;
}) {
  const count = activeFilterCount(filters);

  const selectChips = FILTERS.flatMap(filter => {
    const options = filter.build(scopedRows, today);
    return filters[filter.key].map(value => {
      const option = options.find(candidate => candidate.value === value);
      const chipLabel = option?.chip ?? value;
      return (
        <FilterChip
          key={`${filter.key}:${value}`}
          label={filter.label}
          value={chipLabel}
          removeLabel={`Remove the ${filter.label} filter, ${chipLabel}`}
          onRemove={() => onChange(removeFilterValue(filters, filter.key, value))}
        />
      );
    });
  });
  const rangeChips = DATE_RANGE_FILTERS.flatMap(filter => {
    const range = filters[filter.key];
    if (!isDateRangeActive(range)) return [];
    const chipLabel = dateRangeChip(range);
    return [(
      <FilterChip
        key={filter.key}
        label={filter.label}
        value={chipLabel}
        removeLabel={`Remove the ${filter.label} filter, ${chipLabel}`}
        onRemove={() => onChange({ ...filters, [filter.key]: emptyDateRange() })}
      />
    )];
  });
  const chips = [...selectChips, ...rangeChips];

  const searchChip = filters.search.trim() ? (
    <FilterChip
      label="Search"
      value={filters.search.trim()}
      removeLabel="Clear the account search"
      onRemove={() => onChange({ ...filters, search: '' })}
    />
  ) : null;

  return (
    <FiltersDropdown
      activeCount={count}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      onClearAll={() => onChange(emptyFilters(filters.search))}
      note="Each filter narrows. Two values in one include either."
      chips={<>{chips}{searchChip}</>}
    >
      {/* One labelled row per group, so a long panel reads as a few questions rather
          than fourteen unrelated selects. */}
      {FILTER_GROUPS.map(group => {
        // A group none of whose controls has an option in scope would be a bare label.
        const groupFilters = FILTERS.filter(filter => filter.group === group.key
          && filter.build(scopedRows, today).length > 0);
        if (!groupFilters.length) return null;
        return (
          <div className="fgroup" role="group" aria-label={group.label} key={group.key}>
            <span className="fgrouplab">{group.label}</span>
            <div className="fgrid">
              {groupFilters.map(filter => (
                <FilterSelect key={filter.key} filter={filter} scopedRows={scopedRows}
                  filters={filters} onChange={onChange} today={today} />
              ))}
            </div>
          </div>
        );
      })}
      <div className="fgroup franges" role="group" aria-label="Dates">
        <span className="fgrouplab">Dates</span>
        {DATE_RANGE_FILTERS.map(filter => {
          const range = filters[filter.key];
          const fromId = `${filter.key}-from`;
          const toId = `${filter.key}-to`;
          const latest = pacificToday();
          return (
            <div className="ffield" key={filter.key}>
              <span className="flab" title={filter.hint}>{filter.label}</span>
              <div className="frangeinputs">
                <label className="visually-hidden" htmlFor={fromId}>
                  {filter.label} from
                </label>
                <input
                  id={fromId}
                  type="date"
                  value={range.from}
                  onChange={event =>
                    onChange(setDateBound(filters, filter.key, 'from', event.target.value))}
                />
                <span className="frangeto" aria-hidden="true">to</span>
                <label className="visually-hidden" htmlFor={toId}>
                  {filter.label} to
                </label>
                <input
                  id={toId}
                  type="date"
                  value={range.to}
                  max={latest}
                  onChange={event =>
                    onChange(setDateBound(filters, filter.key, 'to', event.target.value))}
                />
              </div>
            </div>
          );
        })}
      </div>
    </FiltersDropdown>
  );
}
